import { ContentResponse, ContentType, FindContentMetadataResponse, FindDuplicatesResponse, ImportResponse } from "@authory-packages/core";
import { measurePromise } from "./measurePromise";
import { UniversalAddTrackingEventNames } from "../components/V3ModalUniversalAddContent/UniversalAddContentTracking";
import { forceUseFindContent } from "../shared-logic/forceUseFindContent";

export interface FindDuplicateParams {
    pageUrl: string;
    canonicalUrl?: string;
    type: ContentType;
}

export const findContentByURL = async (
    onFindContent: (url: string, mimeType?: string) => Promise<ImportResponse | undefined>,
    onFindContentSnippets: (url: string) => Promise<FindContentMetadataResponse | undefined>,
    url: string,
    targetContentType: ContentType,
    useSnippetOnly?: boolean,
    isPDFURLImport?: boolean,
    trackEvent?: (eventName: string, data: any) => void,
    onFindDuplicate?: (params: FindDuplicateParams) => Promise<FindDuplicatesResponse | undefined>
) => {
    const urlPromises = [];
    const encodedURL = encodeURI(url);

    let findContentPromise: Promise<ImportResponse | undefined> | undefined;
    let findContentSnippetPromise: Promise<{ res: FindContentMetadataResponse | undefined; duration: number }> | undefined;

    const useFindContentEndpoint = !useSnippetOnly || forceUseFindContent(url);

    // we either fetch snippet or fetch content
    if (useFindContentEndpoint) {
        findContentPromise = onFindContent(encodedURL, isPDFURLImport ? "application/pdf" : undefined).catch(() => undefined);
        urlPromises.push(findContentPromise);
    } else {
        findContentSnippetPromise = measurePromise(() => onFindContentSnippets(encodedURL).catch(() => undefined));
        urlPromises.push(findContentSnippetPromise);
    }

    // Wait for all calls
    await Promise.all(urlPromises.map((res) => res));

    const findContentSnippetPromiseRes = await findContentSnippetPromise;
    const snippetRes = findContentSnippetPromiseRes?.res ? findContentSnippetPromiseRes?.res.snippets[0] : undefined;
    const contentRes = await findContentPromise;

    try {
        if (trackEvent && !isPDFURLImport && findContentSnippetPromiseRes?.res) {
            trackEvent(UniversalAddTrackingEventNames.SnippetsComplete, {
                duration: findContentSnippetPromiseRes?.duration,
                url: encodedURL,
                title: !!snippetRes?.title || !!contentRes?.title,
                image: !!snippetRes?.thumbnailImage || !!contentRes?.previewImage,
                description: !!snippetRes?.description || !!contentRes?.description,
                type: targetContentType,
                status: useFindContentEndpoint ? (!!contentRes ? "success" : undefined) : snippetRes?.status
            });
        }
    } catch {}

    // Duplicate check runs after the metadata call, as that's where the canonical url and the resolved type come from.
    // A failure here is never fatal - we just carry on as if the user has no duplicate.
    let duplicate: ContentResponse | undefined;

    if (onFindDuplicate) {
        const duplicateRes = await onFindDuplicate({
            pageUrl: encodedURL,
            canonicalUrl: snippetRes?.canonicalUrl || undefined,
            type: contentRes?.type ?? targetContentType
        }).catch(() => undefined);

        duplicate = duplicateRes?.content;
    }

    return { contentRes, snippetRes, duplicate };
};
